import { format } from 'date-fns';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import type { McpOAuthGrant } from '@/api/v4/mcp-oauth.api';
import { timeAgo } from '@/features/notifications/utils/time-ago';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Stack } from '@/ui/line/line';

import type { AccountScopedMcpConnectorConfig } from '../mcp-oauth/mcp-oauth-connector-config';
import {
  getClientLabel,
  getDisplayScopes,
} from '../mcp-oauth/mcp-oauth-connection-status';
import { McpOAuthSetupTabs } from '../mcp-oauth/mcp-oauth-setup-tabs';
import { SectionDivider } from '../mcp-oauth/mcp-oauth.styles';
import {
  Card,
  DangerTextButton,
  SetupStepsAnchor,
} from './claude-mcp-connector.styles';
import { ClaudeMcpConnectorHeader } from './claude-mcp-connector-header';

interface ClaudeMcpConnectorConnectedViewProps {
  connectorConfig: AccountScopedMcpConnectorConfig;
  copiedFieldId: string | null;
  grant: McpOAuthGrant;
  onCopy: (value: string, fieldId: string) => void;
  onDisconnect: (grant: McpOAuthGrant) => void;
  setupStepsRef: RefObject<HTMLDivElement | null>;
  showAdvancedSettingsHint: boolean;
  showSetupSteps: boolean;
  onToggleSetupSteps: () => void;
}

export const ClaudeMcpConnectorConnectedView = ({
  connectorConfig,
  copiedFieldId,
  grant,
  onCopy,
  onDisconnect,
  onToggleSetupSteps,
  setupStepsRef,
  showAdvancedSettingsHint,
  showSetupSteps,
}: ClaudeMcpConnectorConnectedViewProps) => {
  const { t } = useTranslation('settings', { keyPrefix: 'accessTokensTab' });
  const clientLabel = getClientLabel(grant, t);
  const displayScopes = getDisplayScopes(grant.scopes, t);

  return (
    <Card>
      <Stack fullWidth gap="14px">
        <ClaudeMcpConnectorHeader
          badge={<ConnectedBadge>{t`mcpOAuth.statusConnected`}</ConnectedBadge>}
          subtitle={t`mcpOAuth.cardSubtitleConnected`}
          title={t`mcpOAuth.cardTitle`}
        />

        <DetailsList>
          <DetailsRow>
            <DetailsLabel>{t`mcpOAuth.connectedDetailsClient`}</DetailsLabel>
            <DetailsValue title={clientLabel}>{clientLabel}</DetailsValue>
          </DetailsRow>
          <DetailsRow>
            <DetailsLabel>{t`mcpOAuth.connectedDetailsConnected`}</DetailsLabel>
            <DetailsValue>
              {format(new Date(grant.createdAt), 'd MMM yyyy')}
            </DetailsValue>
          </DetailsRow>
          <DetailsRow>
            <DetailsLabel>{t`mcpOAuth.connectedDetailsLastUsed`}</DetailsLabel>
            <DetailsValue>
              {grant.lastUsedAt
                ? timeAgo(grant.lastUsedAt)
                : t`table.neverUsed`}
            </DetailsValue>
          </DetailsRow>
          <ScopesSection>
            <DetailsLabel>{t`mcpOAuth.connectedDetailsScopes`}</DetailsLabel>
            <ScopePillRow>
              {displayScopes.map(scope => (
                <ScopePill key={scope}>{scope}</ScopePill>
              ))}
            </ScopePillRow>
          </ScopesSection>
        </DetailsList>

        {showSetupSteps ? (
          <SetupStepsAnchor defaultRef={setupStepsRef} fullWidth>
            <McpOAuthSetupTabs
              connectorConfig={connectorConfig}
              copiedFieldId={copiedFieldId}
              onCopy={onCopy}
              showAdvancedSettingsHint={showAdvancedSettingsHint}
            />
          </SetupStepsAnchor>
        ) : null}

        <SectionDivider />
        <ConnectedActions alignItems="center" fullWidth gap="16px">
          <DangerTextButton onClick={() => onDisconnect(grant)} type="button">
            {t`mcpOAuth.disconnect`}
          </DangerTextButton>
          <MutedTextButton onClick={onToggleSetupSteps} type="button">
            {showSetupSteps
              ? t`mcpOAuth.hideSetupSteps`
              : t`mcpOAuth.havingTroubleLink`}
          </MutedTextButton>
        </ConnectedActions>
      </Stack>
    </Card>
  );
};

const ConnectedBadge = styled('span')`
  background-color: ${({ theme }) => theme.colors.spotlight.icon.green.bg};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.spotlight.icon.green.color};
  flex-shrink: 0;
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 9px;
`;

const DetailsList = styled('dl')`
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 13px;
  margin: 0;
  min-width: 0;
`;

const DetailsRow = styled('div')`
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray.c2};
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-width: 0;
  padding: 8px 0;
`;

const DetailsLabel = styled('dt')`
  color: ${({ theme }) => theme.colors.gray.c10};
  flex-shrink: 0;
`;

const DetailsValue = styled('dd')`
  color: ${({ theme }) => theme.colors.gray.c12};
  flex: 1 1 0;
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ScopesSection = styled('div')`
  padding: 10px 0 4px;
`;

const ScopePillRow = styled('dd')`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 7px 0 0;
`;

const ScopePill = styled('span')`
  background-color: ${({ theme }) => theme.colors.spotlight.surface.default};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.gray.c12};
  font-size: 11.5px;
  padding: 3px 8px;
`;

const ConnectedActions = styled(Flexbox)`
  flex-wrap: wrap;
`;

const MutedTextButton = styled('button')`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.gray.c10};
  cursor: pointer;
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 13px;
  padding: 0;
  text-decoration: underline;
`;
